// src/components/QuickBooks/useQboConnectionMeta.jsx

import { useEffect, useState, useCallback } from "react";
import { getQboConnectionMeta } from "./qboClient.jsx";

// Fallback if the backend doesn't send safetyMs
const SAFETY_MS_FALLBACK = 2 * 60 * 1000; // 2 minutes

function deriveState(raw) {
  if (!raw) {
    return {
      state: "disconnected",
      msRemaining: null,
      minutesRemaining: null,
      safetyMs: SAFETY_MS_FALLBACK,
    };
  }

  const safetyMs = raw.safetyMs ?? SAFETY_MS_FALLBACK;

  // Token is usable if: 
  // 1. isTokenValid is explicitly true, OR
  // 2. connectionState is "connected", OR  
  // 3. status is "connected" OR "expiringSoon" (treat expiring as still connected)
  const tokenUsable =
    raw.isTokenValid === true ||
    raw.connectionState === "connected" ||
    raw.status === "connected" ||
    raw.status === "expiringSoon";  // Treat expiringSoon as connected

  return {
    state: tokenUsable ? "connected" : "disconnected",
    msRemaining: null,
    minutesRemaining: null,
    safetyMs,
  };
}
/**
 * Hook to read QuickBooks connection meta from qboProxy.
 *
 * @param {("sandbox"|"production"|undefined)} qbEnv
 * @param {{ enabled?: boolean, poll?: boolean, pollMs?: number }} options
 */
export function useQboConnectionMeta(
  qbEnv,
  { enabled = true, poll = false, pollMs = 30000 } = {}
) {
  const canRun = enabled && !!qbEnv;
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(canRun);
  const [error, setError] = useState(null);
  const [derived, setDerived] = useState(() =>
    deriveState(null)
  );

  const load = useCallback(async () => {
    if (!canRun) {
      setMeta(null);
      setDerived(deriveState(null));
      setError(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await getQboConnectionMeta({
        qbenv: qbEnv,
        app: "shared",
      });

      const derivedNext = deriveState(data);

      console.log("[useQboConnectionMeta] DEBUG", {
        qbEnv,
        canRun,
        raw: data,
        derived: derivedNext,
        nowIso: new Date().toISOString(),
      });

      setMeta(data);
      setDerived(deriveState(data));
    } catch (err) {
      console.error("useQboConnectionMeta error:", err);
      setMeta(null);
      setDerived(deriveState(null));
      setError(String(err.message || err));
    } finally {
      setLoading(false);
    }
  }, [canRun, qbEnv]);

  // initial + env changes
  useEffect(() => {
    load();
  }, [load]);

  // optional polling
  useEffect(() => {
    if (!poll || !canRun) return;
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
  }, [poll, pollMs, canRun, load]);

  const { state, msRemaining, minutesRemaining } = derived;

  return {
    meta,
    loading,
    error,
    state,
    msRemaining,
    minutesRemaining,
    isConnected: state === "connected",
    isExpiringSoon: state === "expiringSoon",
    isDisconnected: state === "disconnected",
    refresh: load,
  };
}